/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { dateFormat } from "~/constants/dateFormats";
import { createCountFilterDefinition } from "~/hooks/useAppliedFilters/definitionFactories";
import { AppliedFilterDefinition } from "~/hooks/useAppliedFilters/types";
import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { QueryGetPlans } from "./constants";

type DefinitionKey =
  | "creationDate"
  | "status"
  | "includedActions"
  | "scheduleOption";
type PlansQueryState = QueryGetPlans & QueryState;

type Definition = AppliedFilterDefinition<PlansQueryState, DefinitionKey>;

export const createPlansAppliedFilters = (
  planActionNamesById: Record<string, string> = {}
): Definition[] => [
  {
    key: "creationDate",
    label: "Creation Date",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.startDateUTC && queryState.endDateUTC
        ? [queryState.startDateUTC, queryState.endDateUTC]
        : [],
    getValueDisplay: (queryState) =>
      `${dayjs(queryState.startDateUTC).format(dateFormat.shortDate)} - ${dayjs(
        queryState.endDateUTC
      ).format(dateFormat.shortDate)}`,
    clear: () => ({
      startDateUTC: undefined,
      endDateUTC: undefined,
    }),
  },
  createCountFilterDefinition<PlansQueryState, "status">({
    key: "status",
    label: "Status",
    labels: { singular: "Status", plural: "Statuses" },
    getValues: (queryState) => queryState.planStatuses,
    getExcluded: (queryState) => queryState.excludePlanStatuses,
    clear: () => ({ planStatuses: [], excludePlanStatuses: false }),
    applyValuesPatch: (queryState, values) => ({
      planStatuses: values,
      excludePlanStatuses: values.length
        ? queryState.excludePlanStatuses
        : false,
    }),
  }),
  createCountFilterDefinition<PlansQueryState, "includedActions">({
    key: "includedActions",
    label: "Included Actions",
    labels: { singular: "Included Action", plural: "Included Actions" },
    getValues: (queryState) => queryState.planActionIds,
    getExcluded: (queryState) => queryState.excludePlanActionIds,
    getValueLabel: (value) => planActionNamesById[value] ?? value,
    clear: () => ({ planActionIds: [], excludePlanActionIds: false }),
    applyValuesPatch: (queryState, values) => ({
      planActionIds: values,
      excludePlanActionIds: values.length
        ? queryState.excludePlanActionIds
        : false,
    }),
  }),
  createCountFilterDefinition<PlansQueryState, "scheduleOption">({
    key: "scheduleOption",
    label: "Schedule Option",
    labels: { singular: "Schedule Option", plural: "Schedule Options" },
    getValues: (queryState) => queryState.scheduleOptions,
    getExcluded: (queryState) => queryState.excludeScheduleOptions,
    clear: () => ({ scheduleOptions: [], excludeScheduleOptions: false }),
    applyValuesPatch: (queryState, values) => ({
      scheduleOptions: values,
      excludeScheduleOptions: values.length
        ? queryState.excludeScheduleOptions
        : false,
    }),
  }),
];
